@import 'fluid-drawer.theme.less';

@import '../../styles/mixins/fluid-overlay.mixin.less';
@import '../../styles/mixins/fluid-fading-animations.mixin.less';
@import '../../styles/mixins/fluid-transitions.mixin.less';
@import '../../styles/mixins/fluid-common-components.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: block;

  *, ::after, ::before {
    box-sizing: border-box;
  }

  .fluid-drawer {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @drawer-tokens: @drawer-tokens-legacy;
      @drawer();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @drawer-tokens: @drawer-tokens-lm;
      @drawer();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @drawer-tokens: @drawer-tokens-corp;
      @drawer();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @drawer-tokens: @drawer-tokens-portal;
      @drawer();
    }
  }
}


// ================================================================ //
// -- Component Detached Ruleset

@drawer: {
  @dismiss-hover-bg: @drawer-tokens[@dismiss-hover-bg];
  @dismiss-active-bg: @drawer-tokens[@dismiss-active-bg];
  @fade-transition-duration: @transition-durations[medium];

  position: fixed;
  inset: 0;
  font-family: @font-family;
  font-size: @font-size;
  font-weight: @font-weight-regular;
  line-height: @line-height;
  color: @color-text;

  // ==== Hide Overlay & Drawer (when closed)

  &.hide-root {
    user-select: none;
    pointer-events: none;
    visibility: hidden;
    opacity: 0;

    .transition(
      @property: visibility, opacity;
      @duration: 0ms;
      @delay: @fade-transition-duration;
    );
  }

  // ==== Background Overlay

  .page-overlay {
    .full-screen-overlay();

    &.overlay-visible {
      .fade-in(@duration: @fade-transition-duration);
    }

    &.overlay-hidden {
      .fade-out(@duration: @fade-transition-duration);
      overflow: hidden;
    }
  }

  // ==== Drawer Container

  .drawer-container {
    display: flex;
    flex-direction: column;
    position: absolute;
    overflow-y: auto;
    background-color: @drawer-tokens[background-color];
    box-shadow: @drawer-tokens[box-shadow];
    outline: none; // Remove "focus ring" when :focus is transferred on open

    .transition(@property: transform; @duration: short);

    // == Drawer Sizes

    .create-drawer-sizes();

    // == Top & Bottom Drawers

    &.anchor-top,
    &.anchor-bottom {
      width: 100%;
      left: 0;
      right: 0;

      &.drawer-visible {
        transform: translateY(0);
      }
    }

    &.anchor-top {
      top: 0;
      transform: translateY(-100%);
    }

    &.anchor-bottom {
      bottom: 0;
      transform: translateY(100%);
    }

    // == Left & Right Drawers

    &.anchor-left,
    &.anchor-right {
      height: 100%;
      top: 0;
      bottom: 0;

      &.drawer-visible {
        transform: translateX(0);
      }
    }

    &.anchor-left {
      left: 0;
      transform: translateX(-100%);
    }

    &.anchor-right {
      right: 0;
      transform: translateX(100%);
    }

    // == Transition State

    &:not(.transition-state) {
      &.drawer-visible {
        transform: none; // Prevents positioning issues with PopperJS-enabled children
      }
    }
  }

  // ==== Drawer Header

  .drawer-header {
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: sticky;
    top: 0;

    // Remove content's top padding when "close" Button is rendered
    &:not(:empty) + .drawer-content {
      padding-top: 0;
    }
  }

  // ==== Drawer Content

  .drawer-content {
    flex: 1 0 auto;
    width: 100%;

    .create-padding-sizes();
  }

  // ==== Dismiss Button

  .drawer-dismiss {

    .close-button(
      @background-hover: @dismiss-hover-bg;
      @background-active: @dismiss-active-bg;
    );
  }
}


// ================================================================ //
// -- Mixins

.create-drawer-sizes() {
  @size-values: {
    xxs: 15;
    xs: 20;
    sm: 30;
    md: 40;
    lg: 50;
    xl: 75;
    full: 90;
  }

  each(@size-values, {
    &.anchor-top,
    &.anchor-bottom {
      &.size-@{key} {
        height: ~'@{value}vh';
      }
    }
    &.anchor-left,
    &.anchor-right {
      &.size-@{key} {
        width: ~'@{value}vw';
      }
    }
  });
}

.create-padding-sizes() {
  @padding-values: {
    xxs: 8px;
    xs: 16px;
    sm: 24px;
    md: 32px;
    lg: 40px;
    xl: 48px;
  }

  each(@padding-values, {
    &.padding-@{key} {
      padding-top: @value;
      padding-bottom: @value;
      padding-left: @value;
      padding-right: @value;
    }
  });
}
